import { CARD_LABELS, parseFurigana, serializeFurigana, type LessonCard } from "../../shared/schema";

interface Props {
  card: LessonCard;
  onChange: (card: LessonCard) => void;
  onDelete: () => void;
  onCopy: () => void;
  onMove: (direction: -1 | 1) => void;
  onMoveTo: (position: number) => void;
}

export function CardEditor({ card, onChange, onDelete, onCopy, onMove, onMoveTo }: Props) {
  const patchContent = (patch: Partial<LessonCard["content"]>) => onChange({ ...card, content: { ...card.content, ...patch } });
  const isQuestion = ["multiple_choice", "ordering", "fill_blank", "text_input"].includes(card.type);
  return (
    <article className="editor-card" draggable onDragStart={(event) => event.dataTransfer.setData("text/card-id", card.id)}>
      <header className="editor-card__header">
        <span className="drag" aria-hidden="true">⋮⋮</span>
        <label className="position-field" title="卡片所在關卡">
          <span className="sr-only">卡片位置</span>
          <input type="number" min={1} value={card.position} onChange={(event) => onMoveTo(Number(event.target.value))} />
        </label>
        <strong>{CARD_LABELS[card.type]}</strong>
        <div className="header-actions">
          <button type="button" className="icon-button" aria-label="往上移" onClick={() => onMove(-1)}>↑</button>
          <button type="button" className="icon-button" aria-label="往下移" onClick={() => onMove(1)}>↓</button>
          <button type="button" className="icon-button" aria-label="複製卡片" onClick={onCopy}>複製</button>
          <button type="button" className="icon-button danger" aria-label="刪除卡片" onClick={onDelete}>刪除</button>
        </div>
      </header>
      <div className="form-grid">
        <label>卡片標題<input value={card.title} onChange={(e) => onChange({ ...card, title: e.target.value })} /></label>
        <label>階段<select value={card.phase} onChange={(e) => onChange({ ...card, phase: e.target.value as LessonCard["phase"] })}>
          <option value="vocabulary">單字</option><option value="grammar">文法</option><option value="guided_use">帶入單字</option><option value="scenario">情境</option><option value="dialogue">對話</option><option value="practice">實戰練習</option><option value="assessment">測驗</option>
        </select></label>
      </div>
      {!isQuestion && card.type !== "dialogue" && (
        <>
          <label>日文內容（例：私[わたし]は学生[がくせい]です。）
            <textarea value={serializeFurigana(card.content.japanese)} onChange={(e) => patchContent({ japanese: parseFurigana(e.target.value) })} />
          </label>
          <div className="form-grid">
            <label>繁體中文<input value={card.content.meaningZhTw ?? ""} onChange={(e) => patchContent({ meaningZhTw: e.target.value })} /></label>
            <label>英文<input value={card.content.meaningEn ?? ""} onChange={(e) => patchContent({ meaningEn: e.target.value })} /></label>
          </div>
          {card.type === "grammar" && <label>文法說明<textarea value={card.content.explanationZhTw ?? ""} onChange={(e) => patchContent({ explanationZhTw: e.target.value })} /></label>}
        </>
      )}
      {card.type === "dialogue" && <label>對話內容（每行：角色｜日文｜中文）
        <textarea value={(card.content.dialogueTurns ?? []).map((turn) => `${turn.speaker}｜${serializeFurigana(turn.japanese)}｜${turn.meaningZhTw ?? ""}`).join("\n")} onChange={(e) => patchContent({ dialogueTurns: e.target.value.split("\n").filter(Boolean).map((line) => { const [speaker, japanese, meaningZhTw] = line.split("｜"); return { speaker: speaker ?? "", japanese: parseFurigana(japanese ?? ""), meaningZhTw }; }) })} />
      </label>}
      {isQuestion && <label>題目<textarea value={card.content.prompt ?? ""} onChange={(e) => patchContent({ prompt: e.target.value })} /></label>}
      {["multiple_choice", "ordering"].includes(card.type) && <label>{card.type === "ordering" ? "可排列詞語（每行一個）" : "選項（每行一個）"}<textarea value={(card.content.choices ?? []).join("\n")} onChange={(e) => patchContent({ choices: e.target.value.split("\n").map((v) => v.trim()).filter(Boolean) })} /></label>}
      {isQuestion && <label>正確答案<input value={card.content.correctAnswer ?? ""} onChange={(e) => patchContent({ correctAnswer: e.target.value })} /></label>}
      <label className="checkbox"><input type="checkbox" checked={card.required} onChange={(e) => onChange({ ...card, required: e.target.checked })} />必修卡片</label>
    </article>
  );
}
